import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { first } from 'rxjs';

import { UserDetails } from '@bn/shared/core/api';
import { FeatureFlagDirective, MENU_ACCESS, UserPermissionsDirective } from '@bn/shared/directives';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';

import { MenuItem } from '../models/menu-item.model';

// Bootstrap lg — below this the sidebar renders as a mobile drawer and the menu behaves as an accordion.
const MOBILE_BREAKPOINT_PX = 992;

@Component({
  selector: 'bn-layout-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  imports: [NgClass, NgTemplateOutlet, RouterLink, RouterLinkActive, FaIconComponent, FeatureFlagDirective, UserPermissionsDirective, BadgeComponent],
})
export class MainMenuComponent implements OnInit {
  @Input() menuConfig!: () => MenuItem[];
  @Input() childRoutes!: () => Record<string, string>;
  @Input() defaultOpenSections: string[] = [];
  @Input() homeRoute = '/';

  public menuItems: MenuItem[] = [];

  private readonly router = inject(Router);
  private readonly access = inject(MENU_ACCESS, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly openMenuItems = new Map<string, boolean>();

  public ngOnInit(): void {
    if (this.access) {
      this.access
        .getFeatureFlags()
        .pipe(first())
        .subscribe((featureFlags) => this.handleFeatureFlagLogic(featureFlags));
    } else {
      this.menuItems = this.menuConfig();
    }

    this.applyRouteOpenState();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;

      this.applyRouteOpenState();
    });
  }

  public isRouteExcluded(menuItem: MenuItem): boolean {
    if (!menuItem.excludeRoutes?.length) return false;

    const currentUrl = this.router.url.split('#')[0].split(';')[0];

    return menuItem.excludeRoutes.some((route) => {
      const excludedUrl = route.startsWith('/') ? route : `/${route}`;

      return currentUrl === excludedUrl || currentUrl.startsWith(`${excludedUrl}/`) || currentUrl.startsWith(`${excludedUrl}?`);
    });
  }

  public onMenuItemClick(menuItem: MenuItem, isChildItem: boolean): void {
    if (isChildItem || !menuItem.children?.length) return;

    const itemId = this.childRoutes()[menuItem.route ?? ''] ?? menuItem.name;
    const willOpen = !menuItem.isOpen;

    if (willOpen && this.isMobileViewport()) this.closeAllSections();
    this.openMenuItems.set(itemId, willOpen);
    this.syncMenuOpenState();
  }

  // A section header without a route renders as an anchor with no href, so it needs
  // button semantics (role/tabindex/Enter/Space) to stay reachable from the keyboard.
  public isSectionToggle(menuItem: MenuItem): boolean {
    return !menuItem.route && !!menuItem.children?.length;
  }

  public onSectionToggleKeydown(event: Event, menuItem: MenuItem, isChildItem: boolean): void {
    if (isChildItem || !this.isSectionToggle(menuItem)) return;

    event.preventDefault();
    this.onMenuItemClick(menuItem, isChildItem);
  }

  private handleFeatureFlagLogic(featureFlags: UserDetails['FeatureFlags']): void {
    this.menuItems = this.menuConfig()
      .map((menuItem) => {
        if (!menuItem.children || menuItem.isIndependentRootItem) return menuItem;

        const filteredChildren = menuItem.children.filter((child) => {
          if (child.negatedFeatureFlag && featureFlags.includes(child.negatedFeatureFlag)) return false;
          if (child.negatedFeatureFlag && !featureFlags.includes(child.negatedFeatureFlag)) return true;
          if (!child.featureFlag && !child.negatedFeatureFlag) return true;
          return !!(child.featureFlag && featureFlags.includes(child.featureFlag));
        });

        if (filteredChildren.length === 0) return null;

        return { ...menuItem, children: filteredChildren };
      })
      .filter((menuItem) => menuItem !== null);

    this.syncMenuOpenState();
  }

  private applyRouteOpenState(): void {
    const baseRoute = window.location.pathname.substring(1)?.split('/')?.at(0) || this.homeRoute;
    const owningSection = baseRoute === this.homeRoute ? undefined : this.childRoutes()[baseRoute];

    if (owningSection !== undefined) {
      if (this.isMobileViewport()) this.closeAllSections();
      this.openMenuItems.set(owningSection, true);
    } else if (this.isMobileViewport()) {
      // Accordion on mobile: apply a default only when nothing is open yet, and only one.
      const firstDefault = this.defaultOpenSections.find((section) => this.openMenuItems.get(section) !== false);
      const anySectionOpen = this.menuItems.some((item) => item.children?.length && (this.openMenuItems.get(item.name) ?? item.isOpen === true));

      if (firstDefault && !anySectionOpen) this.openMenuItems.set(firstDefault, true);
    } else {
      this.defaultOpenSections.forEach((section) => {
        if (this.openMenuItems.get(section) !== false) this.openMenuItems.set(section, true);
      });
    }

    this.syncMenuOpenState();
  }

  private closeAllSections(): void {
    this.menuItems.forEach((item) => {
      if (item.children?.length) this.openMenuItems.set(item.name, false);
    });
  }

  private isMobileViewport(): boolean {
    return window.innerWidth < MOBILE_BREAKPOINT_PX;
  }

  private syncMenuOpenState(): void {
    this.menuItems = this.menuItems.map((item) => ({
      ...item,
      isOpen: this.openMenuItems.get(item.name) ?? item.isOpen === true,
    }));
  }
}
